import { useState, useEffect, useRef, useCallback } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import debounce from 'lodash/debounce';
import { getConfig } from '../AppConfig';

const config = getConfig();

let configured = false;
let placesLibPromise = null;

function getPlacesLibrary() {
  if (!placesLibPromise) {
    if (!configured) {
      setOptions({ key: config.keys.googleAutocomplete, v: 'weekly' });
      configured = true;
    }
    placesLibPromise = importLibrary('places').catch((err) => {
      placesLibPromise = null;
      throw err;
    });
  }
  return placesLibPromise;
}

export default function useGooglePlaces({
  regionCodes = ['us'],
  debounceMs = 300,
} = {}) {
  const [placePredictions, setPlacePredictions] = useState([]);
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] =
    useState(false);
  const placesLibRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    getPlacesLibrary()
      .then((placesLib) => {
        if (!mounted) return;
        placesLibRef.current = placesLib;
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  const regionCodesRef = useRef(regionCodes);
  regionCodesRef.current = regionCodes;

  const debouncedFetch = useRef(
    debounce(async (input) => {
      const placesLib = placesLibRef.current;
      if (!placesLib || !input) {
        setPlacePredictions([]);
        setIsPlacePredictionsLoading(false);
        return;
      }

      try {
        const { suggestions } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes: regionCodesRef.current,
          });

        setPlacePredictions(
          suggestions
            .filter((s) => s.placePrediction)
            .map((s) => ({
              place_id: s.placePrediction.placeId,
              description: s.placePrediction.text.text,
            }))
        );
      } catch {
        setPlacePredictions([]);
      }

      setIsPlacePredictionsLoading(false);
    }, debounceMs)
  ).current;

  useEffect(
    () => () => {
      debouncedFetch.cancel();
    },
    [debouncedFetch]
  );

  const getPlacePredictions = useCallback(
    (request) => {
      setIsPlacePredictionsLoading(true);
      debouncedFetch(request?.input || request);
    },
    [debouncedFetch]
  );

  const fetchPlaceDetails = useCallback(async (placeId) => {
    const placesLib = placesLibRef.current;
    if (!placesLib || !placeId) return null;

    const place = new placesLib.Place({ id: placeId });
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents', 'location'],
    });
    return place;
  }, []);

  return {
    placePredictions,
    isPlacePredictionsLoading,
    getPlacePredictions,
    fetchPlaceDetails,
  };
}
